import type { TFunction } from 'i18next';
import { TEMPLATE_COLOR_PALETTE } from '../constants/templateColors';
import {
  SessionType,
  type CreateInterventionTemplateRequest,
  type InterventionTemplateResponse,
  type MandatoryStaffCompetenceRequirement,
  type UpdateInterventionTemplateRequest
} from '../api/generated';

export const DEFAULT_REQUIREMENT_COUNT = 1;
export const DEFAULT_PROCESSING_MINUTES = 0;

// The same competence may appear in several mandatory rows (e.g. two nurses, one with
// preparation time). Rows therefore carry a client-only `rowId` so React keys and
// updates stay stable; it is stripped before the form is saved.
export type MandatoryRow = MandatoryStaffCompetenceRequirement & { rowId: string };

export type InterventionFormState = Omit<
  InterventionTemplateResponse,
  'mandatoryStaffCompetenceRequirements'
> & {
  mandatoryStaffCompetenceRequirements: MandatoryRow[];
};

export const emptyIntervention = (): InterventionTemplateResponse => ({
  id: crypto.randomUUID(),
  name: '',
  color: TEMPLATE_COLOR_PALETTE[0].strokeColor,
  // Interventions no longer carry a user-facing icon; kept undefined for payload shape.
  icon: undefined,
  durationMinutes: 30,
  sessionType: SessionType.Individual,
  mandatoryStaffCompetenceRequirements: [],
  flexStaffCompetenceRequirements: [],
  roomRequirements: []
});

// Form rows are single-profession; backend supports multi-profession requirements,
// so an existing row's profession is the first id in `staffProfessionIds`.
export const firstProfessionId = (req: MandatoryStaffCompetenceRequirement): string | undefined =>
  req.staffProfessionIds?.[0];

export const toFormState = (iv: InterventionTemplateResponse): InterventionFormState => ({
  ...iv,
  mandatoryStaffCompetenceRequirements: iv.mandatoryStaffCompetenceRequirements.map((req) => ({
    ...req,
    rowId: crypto.randomUUID()
  }))
});

export const toInterventionResponse = (
  form: InterventionFormState
): InterventionTemplateResponse => ({
  ...form,
  mandatoryStaffCompetenceRequirements: form.mandatoryStaffCompetenceRequirements.map((row) => ({
    staffProfessionIds: row.staffProfessionIds,
    count: row.count,
    preProcessingMinutes: row.preProcessingMinutes,
    postProcessingMinutes: row.postProcessingMinutes
  }))
});

// Create and update accept the same payload shape; both mappers delegate here so the
// field-by-field mapping lives in one place.
const toTemplateRequestPayload = (
  iv: InterventionTemplateResponse
): CreateInterventionTemplateRequest => ({
  name: iv.name,
  color: iv.color,
  icon: iv.icon,
  durationMinutes: iv.durationMinutes,
  sessionType: iv.sessionType,
  roomRequirements: iv.roomRequirements,
  mandatoryStaffCompetenceRequirements: iv.mandatoryStaffCompetenceRequirements,
  flexStaffCompetenceRequirements: iv.flexStaffCompetenceRequirements
});

export const toCreateRequest = (
  iv: InterventionTemplateResponse
): CreateInterventionTemplateRequest => toTemplateRequestPayload(iv);

export const toUpdateRequest = (
  iv: InterventionTemplateResponse
): UpdateInterventionTemplateRequest => toTemplateRequestPayload(iv);

// The same competence may be required in several rows; aggregate them into one "N× Name" entry.
export const formatRequiredCompetences = (
  iv: InterventionTemplateResponse,
  nameById: Map<string, string>
): string => {
  const countByProfession = new Map<string, number>();
  iv.mandatoryStaffCompetenceRequirements.forEach((req) => {
    const id = req.staffProfessionIds?.[0];
    if (!id) return;
    countByProfession.set(
      id,
      (countByProfession.get(id) ?? 0) + (req.count ?? DEFAULT_REQUIREMENT_COUNT)
    );
  });
  return Array.from(countByProfession.entries())
    .map(([id, count]) => `${count}× ${nameById.get(id) ?? id}`)
    .join(', ');
};

export const formatFlexCompetences = (
  iv: InterventionTemplateResponse,
  nameById: Map<string, string>,
  t: TFunction
): string =>
  iv.flexStaffCompetenceRequirements
    .map((req) => {
      const id = req.staffProfessionId;
      if (!id) return null;
      const count = req.count ?? DEFAULT_REQUIREMENT_COUNT;
      return `+ ${nameById.get(id) ?? id} (${t('config-iv-flex-of-them', { n: count })})`;
    })
    .filter((line): line is string => line !== null)
    .join(', ');
